import { css } from "@emotion/css";
import type { PageAction, PrimaryPageAction } from "@octopusdeploy/design-system-components";
import { Divider, useMenuState, CustomMenu, MenuItemButton, ActionButton, ActionButtonType, Callout, Dialog, SimpleDataTable, useShowSnackbar } from "@octopusdeploy/design-system-components";
import { space, themeTokens } from "@octopusdeploy/design-system-tokens";
import { useAggregateAPIOperationStatus, useMutation, useQuery } from "@octopusdeploy/octopus-react-client";
import type { ActionTemplateParameterResource, BlueprintResource, CreateProcessTemplateVersionBffCommand, GitBranchResource, GitPersistenceSettings, GitRef, GitRefResource, IconMetadataResource, IconSvgResource, IProcessResource, ModifyProcessTemplateCommand, PlatformHubConnectionConfigurationResource, ProcessTemplateVersionResource, Repository, ShareTemplateToManySpacesBffCommand, SpaceResource, ValidateGitRefV2Response, WorkerPoolsSummaryResource, } from "@octopusdeploy/octopus-server-client";
import { isProcessTemplateParameterResource, canCommitTo, GetPrimaryPackageReference, Permission } from "@octopusdeploy/octopus-server-client";
import type { QueryParamValuesSetter } from "@octopusdeploy/portal-routes";
import { links } from "@octopusdeploy/portal-routes";
import { noOp } from "@octopusdeploy/utilities";
import { isEqual, keyBy } from "lodash";
import pluralize from "pluralize";
import * as React from "react";
import { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { BlueprintParametersTab, buildBlueprintParametersActions } from "~/areas/blueprints/BlueprintParametersTab";
import { EditProcessTemplateSettingsTab } from "~/areas/blueprints/EditProcessTemplateSettingsTab";
import { lastAccessedPlatformHubGitRef } from "~/areas/blueprints/LastAccessedPlatformHubGitRef";
import { PlatformHubPageHeaderBranchSelector } from "~/areas/blueprints/PlatformHubPageHeaderBranchSelector";
import { PublishTemplateDialog } from "~/areas/blueprints/PublishTemplateDialog";
import type { SharedSpaces } from "~/areas/blueprints/ShareTemplateDialog";
import { ShareTemplateDialog } from "~/areas/blueprints/ShareTemplateDialog";
import { StopShareTemplateDialog } from "~/areas/blueprints/StopSharingTemplateDialog";
import { useRequiredPlatformHubGitRef } from "~/areas/blueprints/usePlatformHubGitRef";
import type { ProcessBlueprintContextProps } from "~/areas/projects/components/Process/Blueprints/BlueprintContext";
import { buildProcessBlueprintContextProps, ProcessBlueprintContext } from "~/areas/projects/components/Process/Blueprints/BlueprintContext";
import { isRunOnServerOrWorkerPool, loadAvailableWorkerPools, whereConfiguredToRun } from "~/areas/projects/components/Process/Common/CommonProcessHelpers";
import { useProcessContext } from "~/areas/projects/components/Process/Contexts/ProcessContext";
import { ProcessController } from "~/areas/projects/components/Process/Contexts/ProcessController";
import { useProcessErrorActions } from "~/areas/projects/components/Process/Contexts/ProcessErrors/ProcessErrorsContext";
import { createDefaultFilter, ProcessQueryStringController, useProcessQueryStringContext } from "~/areas/projects/components/Process/Contexts/ProcessQueryString/ProcessQueryStringContext";
import { useProcessWarningActions } from "~/areas/projects/components/Process/Contexts/ProcessWarnings/ProcessWarningsContext";
import { hasSteps } from "~/areas/projects/components/Process/Contexts/processModelSelectors";
import ProcessListLayout from "~/areas/projects/components/Process/ProcessListLayout";
import ProcessStepsLayout, { intentFromFilter, ProcessPageIntent } from "~/areas/projects/components/Process/ProcessStepsLayout";
import type { ProcessStepsLayoutLoaderLookupData } from "~/areas/projects/components/Process/ProcessStepsLayoutLoader";
import { emptyVariableSetResource } from "~/areas/projects/components/Process/ProcessStepsLayoutLoader";
import type { BlueprintProcessIdentifier } from "~/areas/projects/components/Process/types";
import { ExecutionLocation, isBlueprintProcessIdentifier, toBlueprintProcessIdentifier } from "~/areas/projects/components/Process/types";
import { GitRefChip } from "~/areas/projects/components/Releases/GitRefChip/GitRefChip";
import { ContextAddStepButton } from "~/areas/projects/components/Steps/index";
import type { GetCommitButtonProps } from "~/areas/projects/components/VersionControl/CommitButton";
import { GetCommitButton } from "~/areas/projects/components/VersionControl/CommitButton";
import type { CommitMessageWithDetails } from "~/areas/projects/components/VersionControl/CommitMessageWithDetails";
import { getFormattedCommitMessage } from "~/areas/projects/components/VersionControl/CommitMessageWithDetails";
import { PersistenceSettingsContextProvider, usePersistenceSettingsContext } from "~/areas/projects/context/PersistenceSettingsContext";
import { repository } from "~/clientInstance";
import type { ActionPlugin } from "~/components/Actions/pluginRegistry";
import { DropDownIcon } from "~/components/Button/DropDownIcon/DropDownIcon";
import Chip from "~/components/Chips/Chip";
import type { DoBusyTask, Errors } from "~/components/DataBaseComponent/index";
import { useDoBusyTaskEffect } from "~/components/DataBaseComponent/index";
import { useLegacyDoBusyTask } from "~/components/DataBaseComponent/useLegacyDoBusyTask";
import { useDialogTrigger } from "~/components/Dialog/DialogTrigger";
import { LegacyForm } from "~/components/FormPaperLayout/LegacyForm";
import OnboardingPage from "~/components/GettingStarted/OnboardingPage";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import { PageContent } from "~/components/PageContent/PageContent";
import { hasPermission } from "~/components/PermissionCheck/PermissionCheck";
import { PermissionCheck } from "~/components/PermissionCheck/index";
import { Note } from "~/components/form/index";
import * as tenantTagsets from "~/components/tenantTagsets";
import useLocalStorage from "~/hooks/useLocalStorage";
import type { GlobalState } from "~/store/reducers";
import DateFormatter from "~/utils/DateFormatter/index";
import StringHelper from "~/utils/StringHelper";
interface EditProcessTemplateProps {
    loaderData: LoaderData;
    slug: string;
    queryParams: EditProcessTemplateQueryParams;
    setQueryParams: QueryParamValuesSetter<EditProcessTemplateQueryParams>;
    scrollAreaRef?: React.RefObject<HTMLDivElement>;
}
type EditProcessTemplateActiveTab = "process" | "parameters" | "settings" | "versions";
export interface EditProcessTemplateQueryParams {
    gitRef: string;
    activeTab: EditProcessTemplateActiveTab;
}
interface EditWithAllContextProps extends EditProcessTemplateProps {
    doBusyTask: DoBusyTask;
    errors: Error[];
    busy: boolean;
}
interface EditProcessTemplateInternalProps {
    cleanModel: BlueprintResource;
    model: BlueprintResource;
    doBusyTask: DoBusyTask;
    errors: Error[];
    busy: boolean;
    lookupData: EditProcessTemplateLookupData;
    icons: IconSvgResource[];
    iconMetadata: IconMetadataResource | undefined;
    onChange: (blueprint: BlueprintResource) => void;
    gitRefResource: GitRefResource | undefined;
    gitRef: GitRef;
    setGitRef: (gitRef: string) => void;
    gitPersistenceSettings: GitPersistenceSettings;
    activeTab: EditProcessTemplateActiveTab;
    setActiveTab: (value: EditProcessTemplateActiveTab) => void;
    reloadComponent: () => void;
}
interface EditProcessTemplateLookupData extends ProcessStepsLayoutLoaderLookupData {
    spaces: SpaceResource[];
}
function EditWithAllContext({ loaderData, slug, doBusyTask, busy, errors, queryParams, setQueryParams }: EditWithAllContextProps) {
    const [gitRef, setGitRef, validatedGitRef] = useRequiredPlatformHubGitRef(loaderData.platformHubConnectionConfiguration.PersistenceSettings, queryParams, setQueryParams, loaderData.initialValidationResult);
    const [lookupData, setLookupData] = useState<EditProcessTemplateLookupData>({ ...defaultLookups, templates: loaderData.processTemplate.Parameters });
    const [icons, setIcons] = useState<IconSvgResource[]>([]);
    const [iconMetadata, setIconMetadata] = useState<IconMetadataResource>();
    const [reloadKey, setReloadKey] = useState<number>(0);
    const { result: cleanModel, refetch: reloadProcessTemplate } = useQuery(async (repository) => {
        const result = await repository.Blueprints.get(slug, gitRef);
        setModel(result);
        return result;
    }, [slug, gitRef], "Get process template", {
        initialResult: loaderData.processTemplate,
    });
    const [model, setModel] = useState<BlueprintResource>(cleanModel);
    const [blueprintContextProps, setBlueprintContextProps] = useState<ProcessBlueprintContextProps>(buildProcessBlueprintContextProps(cleanModel));
    const incrementReloadKey = () => {
        setReloadKey(reloadKey + 1);
    };
    const refreshLookups = useCallback(async () => {
        const environments = repository.Environments.all();
        const tagSets = tenantTagsets.getAll();
        setLookupData({
            ...defaultLookups,
            environmentsById: keyBy(await environments, "Id"),
            tagSets: await tagSets,
            machineRoles: await repository.MachineRoles.all(),
            tagIndex: await tenantTagsets.getTagIndex(),
            spaces: await repository.Spaces.all(),
        });
    }, []);
    const onChange = useCallback((blueprint: BlueprintResource) => {
        const context = buildProcessBlueprintContextProps(blueprint);
        setBlueprintContextProps(context);
        setLookupData({ ...lookupData, templates: blueprint.Parameters });
        setModel(blueprint);
    }, [lookupData]);
    useDoBusyTaskEffect(doBusyTask, async () => {
        await refreshLookups();
        const icons = await repository.Icons.getIcons();
        setIcons(icons);
        const iconMetadata = await repository.Icons.getIconMetadata();
        setIconMetadata(iconMetadata);
    }, [refreshLookups]);
    const processIdentifier: BlueprintProcessIdentifier = useMemo(() => toBlueprintProcessIdentifier(slug, gitRef), [slug, gitRef]);
    const setActiveTab = useCallback((value) => setQueryParams((prev) => ({ ...prev, activeTab: value })), [setQueryParams]);
    if (!validatedGitRef) {
        return <PageContent header={{ title: StringHelper.ellipsis }}>{null}</PageContent>;
    }
    return (<ProcessBlueprintContext.Provider value={blueprintContextProps}>
            <PersistenceSettingsContextProvider {...loaderData.platformHubConnectionConfiguration.PersistenceSettings}>
                <ProcessController layoutActions={{ refreshLookupData: refreshLookups }} doBusyTask={doBusyTask} processIdentifier={processIdentifier} process={cleanModel} reloadProcess={reloadProcessTemplate} // Only used when updating step templates which blueprints can't do yet, so this is actually unused
     modifyProcess={() => Promise.resolve()} // This function is never used for blueprints since the save logic is handled outside the process context
    >
                    {() => {
            return (<ProcessQueryStringController initialQueryFilter={createDefaultFilter()}>
                                {() => {
                    return (<EditProcessTemplatePageInternal cleanModel={cleanModel} model={model} doBusyTask={doBusyTask} busy={busy} errors={errors} lookupData={lookupData} gitRefResource={validatedGitRef?.GitRef} gitRef={gitRef} setGitRef={setGitRef} gitPersistenceSettings={loaderData.platformHubConnectionConfiguration.PersistenceSettings} icons={icons} iconMetadata={iconMetadata} onChange={onChange} activeTab={queryParams.activeTab} setActiveTab={setActiveTab} key={reloadKey} reloadComponent={incrementReloadKey}/>);
                }}
                            </ProcessQueryStringController>);
        }}
                </ProcessController>
            </PersistenceSettingsContextProvider>
        </ProcessBlueprintContext.Provider>);
}
const defaultLookups: EditProcessTemplateLookupData = {
    includedScriptModules: [],
    lifecyclePreview: null,
    environmentsById: {},
    channelsById: null,
    tagSets: [],
    workerPoolsById: {},
    machineRoles: [],
    tagIndex: {},
    userOnboarding: null,
    workerPoolsSummary: {
        WorkerPoolSummaries: [],
        TotalMachines: 0,
        TotalDisabledMachines: 0,
        MachineHealthStatusSummaries: {
            Healthy: 0,
            Unavailable: 0,
            Unknown: 0,
            HasWarnings: 0,
            Unhealthy: 0,
        },
        MachineEndpointSummaries: {
            None: 0,
            TentaclePassive: 0,
            TentacleActive: 0,
            Ssh: 0,
            OfflineDrop: 0,
            AzureWebApp: 0,
            AzureCloudService: 0,
            AzureServiceFabricCluster: 0,
            Kubernetes: 0,
            KubernetesTentacle: 0,
            StepPackage: 0,
        },
        DeploymentTargetSummaries: {
            Healthy: 0,
            Unavailable: 0,
            Unknown: 0,
            HasWarnings: 0,
            Unhealthy: 0,
        },
        TentacleUpgradesRequired: false,
        MachineIdsForCalamariUpgrade: [],
        MachineIdsForTentacleUpgrade: [],
    },
    projectTriggers: [],
    processVariableSet: emptyVariableSetResource(""), // Empty spaceId while variable sets are space-scoped
    libraryVariableSets: [],
    templates: [],
    spaces: [],
};
interface ProcessStepsOrListLayoutProps {
    lookupData: ProcessStepsLayoutLoaderLookupData;
    doBusyTask: DoBusyTask;
    errors: Error[];
    busy: boolean;
    gitRefResource: GitRefResource | undefined;
    scrollAreaRef?: React.RefObject<HTMLDivElement>;
}
function ProcessStepsOrListLayout({ lookupData, busy, doBusyTask, errors, gitRefResource, scrollAreaRef }: ProcessStepsOrListLayoutProps) {
    const processQueryStringContext = useProcessQueryStringContext();
    const isBuiltInWorkerEnabled = useSelector((state: GlobalState) => state.configurationArea.features.isBuiltInWorkerEnabled);
    const intent = intentFromFilter(processQueryStringContext.state.queryFilter);
    if (intent === ProcessPageIntent.Unknown) {
        return (<ProcessListLayout gitRef={gitRefResource?.CanonicalName} onProcessSave={() => Promise.resolve()} titleAccessory={undefined} releaseCreationPackageStepId={undefined} lookups={lookupData} doBusyTask={doBusyTask} isBuiltInWorkerEnabled={isBuiltInWorkerEnabled} gitRefResource={gitRefResource}/>);
    }
    return (<ProcessStepsLayout gitRef={gitRefResource?.CanonicalName} titleAccessory={undefined} stepSlug={undefined} setShowK8sStatusItem={undefined} onProcessSave={() => Promise.resolve()} changeGitRef={noOp} // todo
     refreshModel={() => Promise.resolve(true)} projectStatus={undefined} includedLibraryVariableSetIds={[]} releaseCreationPackageStepId={undefined} lookups={lookupData} errors={errors} busy={busy} doBusyTask={doBusyTask} isBuiltInWorkerEnabled={isBuiltInWorkerEnabled} gitRefResource={gitRefResource} isTenanted={true} onValidationError={() => scrollAreaRef?.current?.scrollTo({ top: 0, behavior: "smooth" })}/>);
}
function applyCommonLogicToProcessResource(process: IProcessResource, workerPoolsSummary: WorkerPoolsSummaryResource, getPluginForAction: (actionId: string) => ActionPlugin) {
    const availableWorkerPools = loadAvailableWorkerPools(workerPoolsSummary);
    process.Steps.forEach((step) => {
        step.Actions.forEach((action) => {
            const plugin = getPluginForAction(action.Id);
            const runOn = whereConfiguredToRun(!!step.Properties["Octopus.Action.TargetRoles"], action, availableWorkerPools, plugin);
            if (runOn) {
                if (!isRunOnServerOrWorkerPool(runOn)) {
                    action.Container = { FeedId: null, Image: null, GitUrl: null, Dockerfile: null };
                }
                else {
                    if (runOn.executionLocation === ExecutionLocation.OctopusServer || runOn.executionLocation === ExecutionLocation.WorkerPool) {
                        step.Properties["Octopus.Action.TargetRoles"] = "";
                    }
                    if (runOn.executionLocation !== ExecutionLocation.WorkerPool && runOn.executionLocation !== ExecutionLocation.WorkerPoolForRoles) {
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        action.WorkerPoolId = null!;
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        action.WorkerPoolVariable = null!;
                    }
                    action.Container = runOn.container;
                }
            }
            if (!action.Name || action.Name.length === 0) {
                const primaryPackage = GetPrimaryPackageReference(action.Packages);
                if (primaryPackage) {
                    action.Name = primaryPackage.PackageId;
                }
            }
        });
    });
}
function EditProcessTemplatePageInternal({ cleanModel, model, doBusyTask, busy, errors, lookupData, icons, iconMetadata, onChange, gitRefResource, gitRef, setGitRef, gitPersistenceSettings, activeTab, setActiveTab, reloadComponent, }: EditProcessTemplateInternalProps) {
    const persistenceSettings = usePersistenceSettingsContext();
    const process = useProcessContext();
    const processIdentifier = process.state.processIdentifier;
    const errorActions = useProcessErrorActions();
    const warningActions = useProcessWarningActions();
    const navigation = useSpaceAwareNavigation();
    const [commitMessage, setCommitMessage] = useState<CommitMessageWithDetails>({ summary: "", details: "" });
    const [disableDirtyFormChecking, setDisableDirtyFormChecking] = useState<boolean>(false);
    const showSnackbar = useShowSnackbar();
    const { openDialog: openPublishDialog, isOpen: isPublishDialogOpen } = useDialogTrigger();
    const { openDialog: openShareDialog, isOpen: isShareDialogOpen } = useDialogTrigger();
    const { openDialog: openStopShareDialog, isOpen: isStopShareDialogOpen } = useDialogTrigger();
    const [dismissedShareAndPublishCallout, setDismissed] = useLocalStorage(`Octopus.Callout.ProcessTemplate.ShareAndPublish.${cleanModel.Slug}.Dismissed`, false);
    const [openPublishShareMenu, publishShareMenuState, buttonAriaAttributes] = useMenuState();
    const { result: publishedVersions, refetch: reloadPublishedVersions } = useQuery((repository) => repository.Blueprints.getVersions(cleanModel.Slug), [cleanModel.Slug], "Get process template versions");
    const { result: sharedSpaces, refetch: reloadSharedSpaces } = useQuery((repository) => repository.Blueprints.getSharedSpaces(model.Slug), [model.Slug], "Get spaces process template is shared to");
    const isTemplateShared = sharedSpaces?.SharedToAllSpaces || (sharedSpaces && sharedSpaces.SpaceIds.length > 0);
    const { execute: handleSave } = useMutation({
        name: "Publish Process Template Version",
        action: async (repository: Repository, version: string, isPreRelease: boolean) => {
            const publishVersion: CreateProcessTemplateVersionBffCommand = {
                ProcessTemplateId: model.Id,
                Version: version,
                IsPreRelease: isPreRelease,
            };
            await repository.Blueprints.publishVersion(publishVersion);
        },
        afterComplete: async () => {
            showSnackbar("Process template version published");
            reloadPublishedVersions();
            reloadComponent();
        },
    });
    const latestVersionForGitRef = publishedVersions?.find((v) => v.GitRef === gitRef && !v.IsPreRelease);
    const { execute: handleShare } = useMutation({
        name: "Share Process Template Version",
        action: async (repository: Repository, sharedSpaces: SharedSpaces) => {
            const shareCommand: ShareTemplateToManySpacesBffCommand = {
                ProcessTemplateId: model.Id,
                ShareToAllSpaces: sharedSpaces.shareToAll,
                SpaceIds: sharedSpaces.individualSpacesSharedTo,
            };
            return await repository.Blueprints.shareToSpace(shareCommand);
        },
        afterComplete: async (repository, output) => {
            reloadSharedSpaces();
            let snackbarMessage = "Process template";
            if (output.SharedToAllSpaces) {
                snackbarMessage += ` shared to all spaces`;
            }
            else {
                if (output.SharedSpaceIds.length > 0) {
                    snackbarMessage += ` shared to ${pluralize("new space", output.SharedSpaceIds.length, true)}`;
                }
                if (output.SharedSpaceIds.length > 0 && output.UnsharedSpaceIds.length > 0) {
                    snackbarMessage += " and";
                }
                if (output.UnsharedSpaceIds.length > 0) {
                    snackbarMessage += ` removed from ${pluralize("space", output.UnsharedSpaceIds.length, true)}`;
                }
                if (output.SharedSpaceIds.length === 0 && output.UnsharedSpaceIds.length === 0) {
                    snackbarMessage += " sharing required no changes and was not updated";
                }
            }
            showSnackbar(snackbarMessage);
            reloadComponent();
        },
    });
    const sharePublishButton: PageAction = {
        key: "PublishAndShareButton",
        type: "custom",
        content: (<PermissionCheck permission={Permission.PlatformHubEdit}>
                <ActionButton label={"Publish and Share"} onClick={openPublishShareMenu} type={ActionButtonType.Secondary} accessibleName={"PublishAndShare"} menuButtonAttributes={buttonAriaAttributes} icon={<DropDownIcon />} iconPosition={"right"}/>
                <CustomMenu accessibleName={"Publish and Share Menu"} menuState={publishShareMenuState}>
                    <MenuItemButton title={"Publish"} onClick={openPublishDialog}>
                        <div>
                            <span>Publish</span>
                            <Note>Publish versions of this template</Note>
                        </div>
                    </MenuItemButton>
                    <MenuItemButton title={"Share"} onClick={openShareDialog}>
                        <div>
                            <span>Share</span>
                            <Note>Share this template with all or some spaces</Note>
                        </div>
                    </MenuItemButton>
                    <Divider />
                    <MenuItemButton title={"Stop sharing this template"} onClick={openStopShareDialog} disabled={!isTemplateShared}>
                        <span className={styles.stopSharingMenuItem}>Stop sharing this template</span>
                    </MenuItemButton>
                </CustomMenu>
            </PermissionCheck>),
    };
    const onParametersChanged = useCallback((parameters: ActionTemplateParameterResource[]) => {
        const resource: BlueprintResource = { ...model, Parameters: parameters.map((p) => (isProcessTemplateParameterResource(p) ? p : { ...p, Values: [] })) };
        onChange(resource);
    }, [onChange, model]);
    let secondaryActions: PageAction[] = [addStepButton, sharePublishButton];
    if (activeTab === "process") {
        secondaryActions = [addStepButton, sharePublishButton];
    }
    else if (activeTab === "settings") {
        secondaryActions = [sharePublishButton];
    }
    else if (activeTab === "parameters") {
        const parametersActions = buildBlueprintParametersActions({ parameters: model.Parameters || [], onChange: onParametersChanged });
        secondaryActions = [...parametersActions, sharePublishButton];
    }
    const onSettingsChange = useCallback((settings: BlueprintResource) => {
        const resource: BlueprintResource = { ...model, ...settings };
        onChange(resource);
    }, [onChange, model]);
    const processFromContext = process.state.model.process ? process.selectors.getProcessResource() : null;
    if (processFromContext) {
        if (!isEqual(model.Steps, processFromContext.Steps)) {
            const resource: BlueprintResource = { ...model, Steps: processFromContext.Steps };
            onChange(resource);
        }
    }
    let openCommitDialog: (() => void) | undefined = undefined;
    function getDefaultCommitMessage(): string {
        return "Update process template";
    }
    const saveProcess = async (isNavigationConfirmation: boolean, newBranch?: GitBranchResource) => {
        let isValid = true;
        const onSuccess: () => void = () => {
            errorActions.clearErrors();
            warningActions.clearWarnings();
        };
        const onError: (errors: Errors) => void = (errors) => {
            errorActions.setErrors(errors, process.selectors);
            isValid = false;
            warningActions.clearWarnings();
        };
        if (isNavigationConfirmation && openCommitDialog) {
            openCommitDialog();
            return isValid;
        }
        else {
            await doBusyTask(async () => {
                const { modelProcess: clientProcessResource } = process.selectors.getProcessesForMerge();
                applyCommonLogicToProcessResource(clientProcessResource, lookupData.workerPoolsSummary, process.selectors.getActionPlugin);
                const updatedBlueprint: ModifyProcessTemplateCommand = {
                    ...model,
                    Steps: clientProcessResource.Steps,
                    ChangeDescription: getFormattedCommitMessage(commitMessage, getDefaultCommitMessage()),
                };
                await repository.Blueprints.modify(updatedBlueprint);
                setCommitMessage({ summary: "", details: "" });
                process.actions.refreshFromServer();
                showSnackbar("Process template details updated");
            }, { onError, onSuccess });
            if (isValid && !hasSteps(process.state.model)()) {
                if (!isBlueprintProcessIdentifier(processIdentifier)) {
                    throw new Error("This page only supports process templates");
                }
                const gitRef = model.GitRef;
                navigation.navigate(links.editProcessTemplatePage.generateUrl({ slug: processIdentifier.slug }, { gitRef }));
            }
            return isValid;
        }
    };
    const onNewBranchCreating = async (branchName: string) => {
        throw new Error("Process templates don't support branch creating yet");
        // let newBranchResource: GitBranchResource | null = null;
        //
        // try {
        //     newBranchResource = await repository.ProjectBranches.createBranch(process.state.ownerId, branchName, gitRefResource?.CanonicalName ?? "");
        // } catch (ex) {
        //     setDisableDirtyFormChecking(false);
        //     throw ex;
        // }
        //
        // if (newBranchResource) {
        //     setDisableDirtyFormChecking(true);
        //     if (await saveProcess(false, newBranchResource)) {
        //         changeGitRef(branchName);
        //     }
        //     setDisableDirtyFormChecking(false);
        // }
    };
    function getCommitButtonProps(): GetCommitButtonProps {
        return {
            hasPermission: { permission: Permission.PlatformHubEdit },
            gitRef: gitRefResource?.CanonicalName,
            persistenceSettings: persistenceSettings,
            canCommitToGitRef: canCommitTo(gitRefResource),
            defaultCommitMessage: getDefaultCommitMessage(),
            commitMessage: commitMessage,
            updateCommitMessage: (commitMessage: CommitMessageWithDetails) => setCommitMessage(commitMessage),
            commitMessageAccessibleName: `Commit message for saving the process template process`,
            commitDetailsAccessibleName: `Commit details for saving the process template process`,
            commitButtonAccessibleName: `Commit changes to the process template process`,
            onInitializing: (openDialog) => (openCommitDialog = openDialog),
            label: "Commit",
            busyLabel: "Committing",
            onClick: (e) => saveProcess(false),
            disabled: disableDirtyFormChecking ? false : isEqual(model, cleanModel) || busy || !hasPermission(Permission.PlatformHubEdit),
        };
    }
    const commitAction: PrimaryPageAction = {
        type: "custom",
        content: <GetCommitButton {...getCommitButtonProps()} onNewBranchCreating={onNewBranchCreating}/>,
        key: "Get Commit",
    };
    const titleAccessory = <PlatformHubPageHeaderBranchSelector setGitRef={setGitRef} gitRef={gitRef} gitPersistenceSettings={gitPersistenceSettings}/>;
    const shouldShowShareAndPublishCallout = sharedSpaces === null ? false : hasPermission(Permission.PlatformHubEdit) && !dismissedShareAndPublishCallout && cleanModel.Steps.length > 0 && (publishedVersions?.length === 0 || !isTemplateShared);
    return (<>
            <LegacyForm devToolsDirtyTrackingKey="Process Template" confirmNavigateSaveLabel={"Commit changes..."} disableDirtyFormChecking={disableDirtyFormChecking} onSaveClick={saveProcess} savePermission={{ permission: Permission.PlatformHubEdit }} model={model} cleanModel={cleanModel} forceDisableFormSaveButton={busy}>
                {({ FormContent }) => (<PageContent header={{
                title: model.Name ?? "Edit process template",
                primaryAction: commitAction,
                pageActions: secondaryActions,
                contextSelector: titleAccessory,
            }} callout={shouldShowShareAndPublishCallout
                ? { type: "custom", content: <ShareAndPublishCallout onShareClick={openShareDialog} onPublishClick={openPublishDialog} onDismiss={() => setDismissed(true)} templateName={model.Name}/> }
                : undefined} currentTab={activeTab} onTabChanged={setActiveTab} tabs={[
                {
                    label: "Process",
                    value: "process",
                    content: <ProcessStepsOrListLayout lookupData={lookupData} errors={errors} busy={busy} doBusyTask={doBusyTask} gitRefResource={gitRefResource}/>,
                },
                {
                    label: "Parameters",
                    value: "parameters",
                    content: <BlueprintParametersTab parameters={model.Parameters} onChange={onParametersChanged}/>,
                },
                {
                    label: "Settings",
                    value: "settings",
                    content: (<FormContent hideExpandAll={false}>
                                        {" "}
                                        <EditProcessTemplateSettingsTab processTemplate={model} onChange={onSettingsChange} iconSvgResources={icons} iconMetadata={iconMetadata}/>{" "}
                                    </FormContent>),
                },
                {
                    label: "Versions",
                    value: "versions",
                    content: <ProcessTemplateVersions publishedVersions={publishedVersions}/>,
                },
            ]}/>)}
            </LegacyForm>
            <Dialog open={isPublishDialogOpen}>
                <PublishTemplateDialog busy={busy} onSave={handleSave} errors={errors} gitRef={gitRef} processTemplate={model} latestVersionForGitRef={latestVersionForGitRef}/>
            </Dialog>
            <Dialog open={isShareDialogOpen}>
                <ShareTemplateDialog busy={busy} onSave={handleShare} errors={errors} spaces={lookupData.spaces} sharedSpaces={{ shareToAll: sharedSpaces?.SharedToAllSpaces ?? true, individualSpacesSharedTo: sharedSpaces?.SpaceIds ?? [] }}/>
            </Dialog>
            <Dialog open={isStopShareDialogOpen}>
                <StopShareTemplateDialog onSave={handleShare} onClose={openStopShareDialog}/>
            </Dialog>
        </>);
}
interface ShareAndPublishCalloutProps {
    templateName: string;
    onShareClick: () => void;
    onPublishClick: () => void;
    onDismiss: () => void;
}
function ShareAndPublishCallout({ templateName, onPublishClick, onShareClick, onDismiss }: ShareAndPublishCalloutProps) {
    return (<Callout type={"success"} title={`${templateName} was successfully updated.`} canClose={true} onClose={onDismiss}>
            To use the process template in other spaces, you'll need to{" "}
            <a href="#" onClick={(e) => {
            e.preventDefault();
            onPublishClick();
        }}>
                <strong>publish</strong>
            </a>{" "}
            and{" "}
            <a href="#" onClick={(e) => {
            e.preventDefault();
            onShareClick();
        }}>
                <strong>share</strong>
            </a>{" "}
            this template.
        </Callout>);
}
function ProcessTemplateVersions({ publishedVersions }: {
    publishedVersions: ProcessTemplateVersionResource[] | null;
}) {
    if (publishedVersions === null) {
        return null;
    }
    if (publishedVersions.length === 0) {
        return (<OnboardingPage title="No versions found for this template" intro="Create versions of process templates on any branch to test and publish to the rest of your team. To get started on publishing a version, select “Publish” in the top right corner." learnMore={null}/>);
    }
    return (<div>
            <SimpleDataTable columns={[
            {
                title: "Version",
                render: (data) => (<div className={styles.versionContainer}>
                                <b>{data.Version}</b>
                                {data.IsPreRelease ? <Chip backgroundColor={themeTokens.color.chip.filled.background.info}>Pre-release</Chip> : null}
                            </div>),
                columnSize: "medium",
            },
            { title: "Git reference", render: (data) => <GitRefChip vcsRef={{ GitRef: data.GitRef, GitCommit: data.GitCommit }} showCommit={true}/>, columnSize: "medium" },
            { title: "Published", render: (data) => DateFormatter.dateToLongFormat(data.PublishedDate) },
        ]} data={publishedVersions} getRowKey={(data) => data.Version}/>
        </div>);
}
const styles = {
    versionContainer: css({
        display: "flex",
        alignItems: "center",
        gap: space[6],
    }),
    stopSharingMenuItem: css({
        color: themeTokens.color.text.danger,
    }),
};
const addStepButton: PageAction = {
    type: "custom",
    content: <ContextAddStepButton />,
    key: "Add Step",
};
export async function editProcessTemplateLoader(repository: Repository, gitRef: string, slug: string): Promise<LoaderData> {
    const platformHubConnectionConfiguration = await repository.PlatformHubConnectionRepository.get();
    if (!gitRef) {
        gitRef = lastAccessedPlatformHubGitRef.get(platformHubConnectionConfiguration.PersistenceSettings);
    }
    const initialValidationResult = await repository.PlatformHubRepository.validateGitRef(gitRef);
    const processTemplate = await repository.Blueprints.get(slug, gitRef);
    return {
        processTemplate,
        platformHubConnectionConfiguration,
        initialValidationResult,
    };
}
interface LoaderData {
    processTemplate: BlueprintResource;
    platformHubConnectionConfiguration: PlatformHubConnectionConfigurationResource;
    initialValidationResult: ValidateGitRefV2Response;
}
export default function EditProcessTemplatePage(props: EditProcessTemplateProps) {
    const { doBusyTask, status: doBusyTaskStatus } = useLegacyDoBusyTask();
    const { isInProgress, errors } = useAggregateAPIOperationStatus(doBusyTaskStatus);
    return <EditWithAllContext loaderData={props.loaderData} slug={props.slug} doBusyTask={doBusyTask} busy={isInProgress} errors={errors} queryParams={props.queryParams} setQueryParams={props.setQueryParams}/>;
}
